// src/components/FormSettings/FormSubmitSettings/SubmissionTableBase.jsx
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Tooltip,
} from '@mui/material';
import { debounce } from 'lodash';

import { useFieldMetadata } from '../../../contexts/FieldMetadataContext';
import { useFormLayout } from '../../../contexts/FormLayoutContext';
import { useFormSubmissionSettings } from '../../../contexts/FormSubmissionSettingsContext';

import styles from './draftSubmissionTable.module.css';
import { isSystemId, resolveSystemValue } from '../../../utils/systemColumns';
import {
  getProviderColumnDisplayState,
} from '../../../utils/providerOutputValueUtils';
import {
  getProviderColumnDefinition,
} from '../../../integrations/providerColumns';

const MIN_COL_WIDTH = 25;
const DEFAULT_COL_WIDTH = 120;

/* Build a column list from the *current* layout + metadata */
const buildFromLayout = (layoutConfig = {}, meta = {}) =>
  Object.values(layoutConfig)
    .flat()
    .filter((f) => f.type !== 'submit')
    .map((f, idx) => ({
      id: meta[f.id]?.ufi ?? f.id,
      label: meta[f.id]?.label ?? `Field ${idx + 1}`,
      visible: true,
      order: idx,
    }));

// Normalizes persisted columnOrder from settings so the table
// can treat every item as an object.
const normalizeColPrefs = (order = []) => {
  return (Array.isArray(order) ? order : [])
    .filter(Boolean)
    .map((c, i) => {
      if (typeof c === 'string') return { id: c, visible: true, order: i };
      return {
        ...c,
        id: String(c.id || ''),
        visible: c.visible !== false,
        order: Number.isFinite(c.order) ? c.order : i,
      };
    });
};

// Time → ms helper (same as yours)
const toMs = (t) => {
  try {
    if (!t) return 0;
    if (typeof t?.toDate === 'function') return t.toDate().getTime();
    if (t instanceof Date) return t.getTime();
    const ms = new Date(t).getTime();
    return Number.isFinite(ms) ? ms : 0;
  } catch (_) {
    return 0;
  }
};

const isHttpUrlValue = (value) => {
  const text = String(value ?? '').trim();

  if (!/^https?:\/\//i.test(text)) {
    return false;
  }

  try {
    const parsed = new URL(text);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

const getRowCopyIdentity = (row = {}) => {
  return (
    row?.id ||
    row?.submissionId ||
    row?.submissionDocId ||
    'display-table-row'
  );
};

const buildCellCopyId = ({ row, columnId, text, rowIndex = 0 }) => {
  return [
    getRowCopyIdentity(row),
    String(columnId || 'unknown-column'),
    String(rowIndex),
    String(text || ''),
  ].join('::');
};

/**
 * SubmissionTableBase
 * - Pure renderer over provided `rows` (already session-scoped)
 * - Can optionally show pagination footer
 * - Keeps ALL existing column/virtual/system/asset logic
 */
const SubmissionTableBase = ({
  rows = [],
  // preview-only: if true, rows can include null stubs
  allowStubRows = false,

  // pagination (for final UI)
  enablePagination = false,
  page = 0,
  onPageChange = null,
  rowsPerPage = 25,
  onRowsPerPageChange = null,
  totalCount = null,
  rowsPerPageOptions = [10, 25, 50, 100],

  // column prefs
  columnOrder = [],
  columnWidths = {},
  stickyHeader = false,

  // auth token getter for asset previews
  getAuthToken = null,

  // visual constraints
  maxHeight = 400,
}) => {
  const EMPTY_OBJ = React.useMemo(() => ({}), []);
  const EMPTY_ARR = React.useMemo(() => [], []);

  const stableColumnOrder = columnOrder || EMPTY_ARR;
  const stableColumnWidths = columnWidths || EMPTY_OBJ;

  const tokenGetter = useCallback(
    typeof getAuthToken === 'function' ? getAuthToken : async () => null,
    [getAuthToken]
  );

  const [copiedCellId, setCopiedCellId] = useState(null);
  const [hoveredCopyCellId, setHoveredCopyCellId] = useState(null);

  const { fieldsMetadata } = useFieldMetadata();
  const { layoutConfig } = useFormLayout();
  const { updateTableSurfaceSettings } = useFormSubmissionSettings();

  // debounced saver for widths
  const saveWidthsDebounced = useRef(
    debounce((nextWidths) => {
      updateTableSurfaceSettings('addToSubmissionsTable', { columnWidths: nextWidths });
    }, 350)
  ).current;

  // columns – merge designer prefs with live layout (same logic you had)
  const columns = useMemo(() => {
    const fresh = buildFromLayout(layoutConfig, fieldsMetadata);
    const pref = normalizeColPrefs(stableColumnOrder);
    if (pref.length === 0) return fresh;

    const labelById = new Map(fresh.map((f) => [f.id, f.label]));

    let base = pref.map((c, i) => ({
      ...c,
      label: c.label || labelById.get(c.id) || c.id,
      order: Number.isFinite(c.order) ? c.order : i,
    }));

    const baseIds = new Set(base.map((c) => c.id));
    fresh.forEach((f) => {
      if (!baseIds.has(f.id)) {
        base.push({ id: f.id, label: f.label, visible: true, order: base.length });
      }
    });

    base = base.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    base = base.map((c) =>
      isSystemId(String(c.id))
        ? ({ ...c, kind: c.kind || 'system', role: c.role || 'system' })
        : c
    );

    return base;
  }, [stableColumnOrder, layoutConfig, fieldsMetadata]);

  // UFI → type map (attach/image detection)
  const ufiToType = useMemo(() => {
    const map = {};
    try {
      Object.values(fieldsMetadata || {}).forEach((m) => {
        if (m?.ufi) map[m.ufi] = m.type || 'text';
      });
    } catch (_) {}
    return map;
  }, [fieldsMetadata]);

  const initialWidths = useMemo(
    () =>
      columns.reduce((acc, c) => {
        const incoming = stableColumnWidths?.[c.id];
        return { ...acc, [c.id]: incoming ?? DEFAULT_COL_WIDTH };
      }, {}),
    [columns, stableColumnWidths]
  );

  const [colWidths, setColWidths] = useState(initialWidths);
  const [localWidths, setLocalWidths] = useState(initialWidths);
  const [resizing, setResizing] = useState(null);
  const [isResizing, setIsResizing] = useState(false);

  // keep width map in sync if columns change
  useEffect(() => {
    setColWidths((prev) => {
      const next = { ...prev };
      columns.forEach((c) => {
        if (!next[c.id]) next[c.id] = DEFAULT_COL_WIDTH;
      });
      Object.keys(next).forEach((id) => {
        if (!columns.some((c) => c.id === id)) delete next[id];
      });
      return next;
    });
  }, [columns]);

  // sync colWidths with prop columnWidths
  useEffect(() => {
    if (!stableColumnWidths) return;
    setColWidths((prev) => ({ ...prev, ...stableColumnWidths }));
    setLocalWidths((prev) => ({ ...prev, ...stableColumnWidths }));
  }, [stableColumnWidths]);

  // resize handlers (same)
  const startResizing = (columnId, evt) => {
    if (isResizing) return;
    setResizing({
      columnId,
      startX: evt.clientX,
      startWidth: localWidths[columnId] || DEFAULT_COL_WIDTH,
    });
    setIsResizing(true);
  };

  const handleMouseMove = (evt) => {
    if (!resizing) return;
    const { columnId, startX, startWidth } = resizing;
    const deltaX = evt.clientX - startX;
    const newWidth = Math.max(MIN_COL_WIDTH, startWidth + deltaX);

    setLocalWidths((prev) =>
      prev[columnId] === newWidth ? prev : { ...prev, [columnId]: newWidth }
    );

    const header = document.querySelector(`[data-col='${columnId}']`);
    const cells = document.querySelectorAll(`[data-col='${columnId}-body']`);
    if (header) {
      header.style.width = `${newWidth}px`;
      header.style.minWidth = `${MIN_COL_WIDTH}px`;
    }
    cells.forEach((td) => {
      td.style.width = `${newWidth}px`;
      td.style.minWidth = `${MIN_COL_WIDTH}px`;
    });
  };

  const stopResizing = () => {
    if (!resizing) return;
    const { columnId } = resizing;

    const nextWidthsUnpruned = {
      ...colWidths,
      [columnId]: localWidths[columnId],
    };

    const colIds = new Set(columns.map((c) => c.id));
    const nextWidths = Object.fromEntries(
      Object.entries(nextWidthsUnpruned).filter(([id]) => colIds.has(id))
    );

    setColWidths(nextWidths);
    saveWidthsDebounced(nextWidths);

    setResizing(null);
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', stopResizing);
      document.addEventListener('blur', stopResizing);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopResizing);
      document.removeEventListener('blur', stopResizing);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopResizing);
      document.removeEventListener('blur', stopResizing);
    };
  }, [isResizing, resizing, localWidths]);

  useEffect(() => {
    return () => {
      if (saveWidthsDebounced.cancel) saveWidthsDebounced.cancel();
    };
  }, [saveWidthsDebounced]);

  // ---- lightweight asset preview cache ----
  const previewCacheRef = useRef(new Map());
  const [previewTick, setPreviewTick] = useState(0);

  const primePreviews = useCallback(async (assetIds, getToken) => {
    const missing = assetIds.filter((id) => id && !previewCacheRef.current.has(id));
    if (!missing.length) return;

    const token = await getToken?.();
    if (!token) return;

    try {
      const { fetchAssetById } = await import('../../../utils/frontend_assetManager');

      for (const id of missing) {
        try {
          const data = await fetchAssetById(id, token, {
            includeUrls: true,
            dpr: (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1,
            includeMeta: true,
            prefer: 'micro',
          });

          previewCacheRef.current.set(id, {
            previewUrl: data.previewUrl || null,
            meta: data.meta || null,
          });
        } catch (_) {
          previewCacheRef.current.set(id, { previewUrl: null, meta: null });
        }
      }
      setPreviewTick((n) => n + 1);
    } catch (_) {}
  }, []);

  // ---- linkedAssetsJson map per row ----
  const linkedMapByRowRef = useRef(new WeakMap());
  const getLinkedForRow = useCallback((row) => {
    if (!row || typeof row !== 'object') return null;
    const cache = linkedMapByRowRef.current.get(row);
    if (cache) return cache;

    const raw = row?._linkedAssetsJson;
    if (!raw || typeof raw !== 'string') {
      linkedMapByRowRef.current.set(row, null);
      return null;
    }
    try {
      const arr = JSON.parse(raw);
      const map = new Map();
      if (Array.isArray(arr)) {
        arr.forEach((entry) => {
          if (entry && typeof entry === 'object' && entry.ufi) map.set(entry.ufi, entry);
        });
      }
      linkedMapByRowRef.current.set(row, map);
      return map;
    } catch (_) {
      linkedMapByRowRef.current.set(row, null);
      return null;
    }
  }, []);

  // visible sorted columns
  const visibleSortedColumns = useMemo(() => {
    return columns
      .filter((c) => c.visible !== false)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [columns]);

  const unwrapValue = (x) => {
    let curr = x;
    for (let i = 0; i < 2; i++) {
      if (curr && typeof curr === 'object' && 'value' in curr && !Array.isArray(curr)) curr = curr.value;
      else break;
    }
    return curr;
  };

  const extractFirstAssetId = (row, ufi) => {
    const cell = row?.responses?.[ufi];
    const raw = (cell && typeof cell === 'object' && 'value' in cell) ? cell.value : cell;
    const x = unwrapValue(raw);
    if (Array.isArray(x)) {
      const first = x.find((it) => it && typeof it === 'object' && (it.assetId || it.id));
      return first ? (first.assetId || first.id) : null;
    }
    if (x && typeof x === 'object') return x.assetId || x.id || null;
    return null;
  };

  // Copy-to-Clipboard Function with Tooltip Feedback.
  //
  // Enterprise UX rule:
  // - Copy state is scoped to one cell, not the whole table.
  // - This copies already-rendered values only; it does not call providers,
  //   does not create shortlinks, and does not mutate submission records.
  const handleCopy = (text, cellCopyId) => {
    const safeText = String(text ?? '');
    const nextCopiedCellId = cellCopyId || `display-table::${safeText}`;

    if (
      typeof navigator === 'undefined' ||
      !navigator?.clipboard?.writeText
    ) {
      console.error('[SubmissionTableBase] Clipboard API is not available.');
      return;
    }

    navigator.clipboard.writeText(safeText)
      .then(() => {
        console.log('[SubmissionTableBase] Copied to clipboard:', {
          text: safeText,
          cellCopyId: nextCopiedCellId,
        });

        setCopiedCellId(nextCopiedCellId);

        setTimeout(() => {
          setCopiedCellId((current) =>
            current === nextCopiedCellId ? null : current
          );
        }, 1500);
      })
      .catch((err) => {
        console.error('[SubmissionTableBase] Failed to copy text:', err);
      });
  };

  const renderCopyableText = ({
    text,
    row,
    column,
    rowIndex = 0,
    forceCopy = false,
    copyable = false,
  }) => {
    const safeText = String(text ?? '');
    const shouldRenderAsCopy =
      Boolean(safeText) &&
      (
        forceCopy ||
        copyable ||
        isHttpUrlValue(safeText)
      );

    if (!shouldRenderAsCopy) {
      return safeText;
    }

    const cellCopyId = buildCellCopyId({
      row,
      columnId: column?.id,
      text: safeText,
      rowIndex,
    });

    const isCopied = copiedCellId === cellCopyId;
    const isHovered = hoveredCopyCellId === cellCopyId;

    return (
      <Tooltip
        title="Copied!"
        placement="top"
        arrow
        open={isCopied}
        disableFocusListener
        disableHoverListener
        disableTouchListener
      >
        <span
          onClick={(e) => {
            e.stopPropagation();
            handleCopy(safeText, cellCopyId);
          }}
          onMouseEnter={() => {
            setHoveredCopyCellId(cellCopyId);
          }}
          onMouseLeave={() => {
            setHoveredCopyCellId((current) =>
              current === cellCopyId ? null : current
            );
          }}
          style={{
            display: 'inline-block',
            width: '100%',
            cursor: 'pointer',
            color: isCopied ? '#1976d2' : 'inherit',
            fontWeight: isHovered || isCopied ? 700 : 'inherit',
            transition: 'font-weight 120ms ease, color 120ms ease',
          }}
        >
          {safeText}
        </span>
      </Tooltip>
    );
  };

  // prime previews whenever any asset columns are visible
  useEffect(() => {
    const visibleCols = columns.filter((c) => c.visible !== false);
    const thumbCols = visibleCols.filter(
      (c) => (c.kind === 'virtual' && c.role === 'thumb') || String(c.id).startsWith('thumb@')
    );
    const baseAssetCols = visibleCols.filter((c) => {
      const t = ufiToType[c.id];
      return t === 'attach' || t === 'image';
    });

    if (!thumbCols.length && !baseAssetCols.length) return;

    const ids = [];
    for (const row of rows) {
      if (!row) continue;
      for (const col of thumbCols) {
        const ufi = col.ufi || String(col.id).split('@')[1];
        if (!ufi) continue;
        const aid = extractFirstAssetId(row, ufi);
        if (aid) ids.push(aid);
      }
      for (const col of baseAssetCols) {
        const ufi = col.id;
        const aid = extractFirstAssetId(row, ufi);
        if (aid) ids.push(aid);
      }
    }

    primePreviews([...new Set(ids)], tokenGetter);
  }, [rows, columns, previewTick, ufiToType, tokenGetter, primePreviews]);

  // ---- render helpers (system/virtual/base) ----
  const renderCellValue = (ufi, row, stubIndex) => {
    const fieldType = ufiToType[ufi] || 'text';
    if (!row) {
      if (fieldType === 'attach' || fieldType === 'image') {
        return <span style={{ width: 18, height: 18, display: 'inline-block', background: '#eee', borderRadius: 2 }} />;
      }
      return stubIndex === 0 ? 'Example' : '';
    }

    const cell = row?.responses?.[ufi];
    const raw = (cell && typeof cell === 'object' && 'value' in cell) ? cell.value : cell;

    if (fieldType === 'attach' || fieldType === 'image') {
      const aid = extractFirstAssetId(row, ufi);
      if (!aid) {
        return <span style={{ width: 18, height: 18, display: 'inline-block', background: '#eee', borderRadius: 2 }} />;
      }
      const cached = previewCacheRef.current.get(aid);
      const url = cached?.previewUrl || null;
      if (url) {
        return (
          <img
            src={url}
            alt=""
            width={18}
            height={18}
            style={{ objectFit: 'cover', borderRadius: 2 }}
            title={aid}
            aria-label="asset thumbnail"
          />
        );
      }
      return <span title={aid} aria-label="asset thumbnail loading" style={{ width: 18, height: 18, display: 'inline-block', background: '#eee', borderRadius: 2 }} />;
    }

    const normalized = unwrapValue(raw);
    if (normalized == null) return '';
    if (typeof normalized === 'object') {
      return normalized.value != null && typeof normalized.value !== 'object' ? String(normalized.value) : '';
    }
    return String(normalized);
  };

  const renderThumbCell = (row, col) => {
    const ufi = col.ufi || String(col.id).split('@')[1];
    if (!ufi) return '';
    const aid = extractFirstAssetId(row, ufi);
    if (!aid) return '';
    const cached = previewCacheRef.current.get(aid);
    const url = cached?.previewUrl || null;
    if (url) {
      return <img src={url} alt="" width={18} height={18} style={{ objectFit: 'cover', borderRadius: 2 }} title={aid} aria-label="asset thumbnail" />;
    }
    return <span title={aid} aria-label="asset thumbnail loading" style={{ width: 18, height: 18, display: 'inline-block', background: '#eee', borderRadius: 2 }} />;
  };

  const renderVersionCell = (row, col) => {
    const ufi = col.ufi || String(col.id).split('@')[1];
    if (!ufi) return '';
    const linked = getLinkedForRow(row);
    const linkedEntry = linked?.get(ufi) || null;
    if (linkedEntry && typeof linkedEntry.versionNumber !== 'undefined') return String(linkedEntry.versionNumber ?? '');

    const cell = row?.responses?.[ufi];
    const raw = (cell && typeof cell === 'object' && 'value' in cell) ? cell.value : cell;
    const x = unwrapValue(raw);

    if (Array.isArray(x)) {
      const first = x.find((it) => it && typeof it === 'object' && (it.assetId || it.id));
      return String(first?.versionNumber ?? first?.version ?? '');
    }
    if (x && typeof x === 'object') return String(x.versionNumber ?? x.version ?? '');
    return '';
  };

  const renderAssetTypeCell = (row, col) => {
    const ufi = col.ufi || String(col.id).split('@')[1];
    if (!ufi) return '';
    const linked = getLinkedForRow(row);
    const linkedEntry = linked?.get(ufi) || null;
    if (linkedEntry && typeof linkedEntry.assetType !== 'undefined') return String(linkedEntry.assetType ?? '');

    const cell = row?.responses?.[ufi];
    const raw = (cell && typeof cell === 'object' && 'value' in cell) ? cell.value : cell;
    const x = unwrapValue(raw);

    if (Array.isArray(x)) {
      const first = x.find((it) => it && typeof it === 'object' && (it.assetId || it.id));
      return String(first?.assetType ?? '');
    }
    if (x && typeof x === 'object') return String(x.assetType ?? '');
    return '';
  };

  const renderAufFieldCell = (row, col) => {
    const attachUfi = col.ufi || String(col.id).split('@')[1];
    const aufUfi = col.aufUfi || String(col.id).split('@')[2];
    if (!attachUfi || !aufUfi) return '';

    const linked = getLinkedForRow(row);
    const linkedEntry = linked?.get(attachUfi) || null;
    if (linkedEntry && linkedEntry.aufResponses) {
      const v = linkedEntry.aufResponses[aufUfi];
      if (v != null && typeof v !== 'object') return String(v);
      if (Array.isArray(v)) return v.filter(Boolean).join(', ');
    }

    const aid = extractFirstAssetId(row, attachUfi);
    if (!aid) return '';
    const cached = previewCacheRef.current.get(aid);
    const meta = cached?.meta || null;
    if (meta) {
      if (aufUfi in meta && typeof meta[aufUfi] !== 'object') return String(meta[aufUfi] ?? '');
      const rv = meta?.responses?.[aufUfi]?.value;
      if (Array.isArray(rv)) return rv.filter(Boolean).join(', ');
      if (rv != null && typeof rv !== 'object') return String(rv);
    }
    return '';
  };

  const renderAssetIdCell = (row, col) => {
    const ufi = col.ufi || String(col.id).split('@')[1];
    if (!ufi) return '';
    return extractFirstAssetId(row, ufi) || '';
  };

  const renderProviderColumnCell = (row, col, rowIndex = 0) => {
    if (!row) {
      // Builder/stub rows should show the column shape without implying that
      // backend enrichment has run. Real pending text is reserved for actual
      // submitted rows with runtime/backend state.
      return col?.role === 'providerOutput'
        ? 'Generated after submit'
        : '';
    }

    const definition = getProviderColumnDefinition(col);
    const state = getProviderColumnDisplayState(row, col, definition);

    if (!state?.displayText) {
      return '';
    }

    if (state.isPending) {
      return (
        <span
          title={state.status || ''}
          style={{ fontStyle: 'italic', opacity: 0.72 }}
        >
          {state.displayText}
        </span>
      );
    }

    if (state.isError) {
      return (
        <span title={state.error || state.displayText}>
          {state.displayText}
        </span>
      );
    }

    const copyEnabled =
      col?.copyEnabled ??
      definition?.copyEnabled ??
      false;

    return renderCopyableText({
      text: state.displayText,
      row,
      column: col,
      rowIndex,
      copyable: copyEnabled || isHttpUrlValue(state.displayText),
    });
  };

  const virtualRenderers = {
    thumb: renderThumbCell,
    version: renderVersionCell,
    assetType: renderAssetTypeCell,
    aufField: renderAufFieldCell,
    assetId: renderAssetIdCell,
    providerOutput: renderProviderColumnCell,
    providerMetric: renderProviderColumnCell,
  };

  // If pagination is enabled, caller should pass the already-paged rows,
  // but we still support passing full rows + page state:
  const effectiveCount = (typeof totalCount === 'number' && totalCount >= 0)
    ? totalCount
    : rows.filter(Boolean).length;

  return (
    <Paper elevation={0} sx={{ mt: 2, p: 0, width: '100%' }}>
      <TableContainer className={styles.tableContainer} sx={{ maxHeight, overflowX: 'auto' }}>
        <Table stickyHeader={stickyHeader} size="small" sx={{ tableLayout: 'fixed', minWidth: 'max-content' }}>
          <TableHead>
            <TableRow>
              {visibleSortedColumns.map((c) => (
                <TableCell
                  key={c.id}
                  data-col={c.id}
                  className={`${styles.headerCell} ${stickyHeader ? styles.stickyHeader : ''}`}
                  sx={{
                    width: colWidths[c.id],
                    minWidth: MIN_COL_WIDTH,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c.label}
                  <div className={styles.columnResizer} onMouseDown={(e) => startResizing(c.id, e)} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, rIdx) => (
              <TableRow key={rIdx} className={styles.zebraRow}>
                {visibleSortedColumns.map((c) => {
                  let cellNode;

                  if (c.kind === 'system' || isSystemId(String(c.id))) {
                    if (!row) cellNode = allowStubRows ? (rIdx === 0 ? 'Example' : '') : '';
                    else {
                      const text = resolveSystemValue(row, c.id);
                      cellNode = text == null ? '' : String(text);
                    }
                  } else if (c.kind === 'virtual') {
                    const role =
                      c.role ||
                      (String(c.id).startsWith('thumb@') ? 'thumb'
                        : String(c.id).startsWith('assetType@') ? 'assetType'
                        : String(c.id).startsWith('version@') ? 'version'
                        : String(c.id).startsWith('aufField@') ? 'aufField'
                        : String(c.id).startsWith('assetId@') ? 'assetId'
                        : String(c.id).startsWith('providerOutput@') ? 'providerOutput'
                        : String(c.id).startsWith('providerMetric@') ? 'providerMetric'
                        : '');
                    const fn = virtualRenderers[role];
                    cellNode = fn ? fn(row, c, rIdx) : '';
                  } else {
                    cellNode = renderCellValue(c.id, row, rIdx);
                  }

                  const copyableCellNode =
                    typeof cellNode === 'string' && isHttpUrlValue(cellNode)
                      ? renderCopyableText({
                          text: cellNode,
                          row,
                          column: c,
                          rowIndex: rIdx,
                          copyable: true,
                        })
                      : cellNode;

                  return (
                    <TableCell
                      key={c.id}
                      data-col={`${c.id}-body`}
                      className={styles.bodyCell}
                      sx={{
                        minWidth: MIN_COL_WIDTH,
                        maxWidth: colWidths[c.id],
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {copyableCellNode}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {enablePagination && (
        <TablePagination
          component="div"
          count={effectiveCount}
          page={page}
          onPageChange={onPageChange || (() => {})}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange || (() => {})}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </Paper>
  );
};

export default React.memo(SubmissionTableBase);
